import React from 'react';
import { formatScenarioMoney } from '../../utils/cashFlowScenario';

interface ProfitShareBarProps {
  profit: number;
  bonusPool: number;
  employerTax?: number;
  /** All active payroll salaries — not the Compensation-tab owner slice. */
  payrollW2?: number;
  taxCash?: number;
  emptyHint?: string;
}

const ProfitShareBar: React.FC<ProfitShareBarProps> = ({
  profit,
  bonusPool,
  employerTax = 0,
  payrollW2 = 0,
  taxCash = 0,
  emptyHint = 'Enter year-end profit to see how the bonus pool sits against it.',
}) => {
  const tax = Math.max(0, employerTax);
  const pool = Math.max(0, bonusPool);
  const w2 = Math.max(0, payrollW2);
  const companyTax = Math.max(0, taxCash);
  const remaining = profit - w2 - pool - tax - companyTax;
  const used = w2 + pool + tax + companyTax;
  const denom = Math.max(profit, used, 1);
  const pct = (n: number) => Math.min(100, (n / denom) * 100);
  const w2Pct = pct(w2);
  const poolPct = Math.min(100 - w2Pct, pct(pool));
  const empTaxPct = Math.min(100 - w2Pct - poolPct, pct(tax));
  const cashTaxPct = Math.min(100 - w2Pct - poolPct - empTaxPct, pct(companyTax));
  const remainPct = Math.max(0, 100 - w2Pct - poolPct - empTaxPct - cashTaxPct);
  const showFullStack = w2 > 0 || companyTax > 0;

  if (profit <= 0 && used <= 0) {
    return <p className="fin-modal-help">{emptyHint}</p>;
  }

  return (
    <div className="fin-profit-viz" aria-label="Year-end profit uses">
      <div className="fin-profit-viz-track" role="img" aria-hidden>
        {w2Pct > 0 && <span className="fin-profit-viz-seg fin-profit-viz-seg--w2" style={{ width: `${w2Pct}%` }} />}
        {poolPct > 0 && <span className="fin-profit-viz-seg fin-profit-viz-seg--pool" style={{ width: `${poolPct}%` }} />}
        {empTaxPct > 0 && <span className="fin-profit-viz-seg fin-profit-viz-seg--tax" style={{ width: `${empTaxPct}%` }} />}
        {cashTaxPct > 0 && <span className="fin-profit-viz-seg fin-profit-viz-seg--company" style={{ width: `${cashTaxPct}%` }} />}
        {remainPct > 0 && (
          <span
            className={`fin-profit-viz-seg${remaining < 0 ? ' fin-profit-viz-seg--over' : ' fin-profit-viz-seg--remain'}`}
            style={{ width: `${remainPct}%` }}
          />
        )}
      </div>
      <div className="fin-profit-viz-legend">
        {w2 > 0 && (
          <span><i className="fin-profit-viz-dot fin-profit-viz-dot--w2" /> All payroll W-2 {formatScenarioMoney(w2)}</span>
        )}
        <span><i className="fin-profit-viz-dot fin-profit-viz-dot--pool" /> Bonus pool {formatScenarioMoney(pool)}</span>
        {tax > 0 && (
          <span><i className="fin-profit-viz-dot fin-profit-viz-dot--tax" /> Employer tax {formatScenarioMoney(tax)}</span>
        )}
        {companyTax > 0 && (
          <span><i className="fin-profit-viz-dot fin-profit-viz-dot--company" /> Tax cash {formatScenarioMoney(companyTax)}</span>
        )}
        <span>
          <i className={`fin-profit-viz-dot${remaining < 0 ? ' fin-profit-viz-dot--over' : ' fin-profit-viz-dot--remain'}`} />
          {remaining < 0 ? 'Over profit' : showFullStack ? 'Left for owners' : 'Left after bonuses'} {formatScenarioMoney(remaining)}
        </span>
      </div>
    </div>
  );
};

export default ProfitShareBar;
